import { Children, cloneElement, isValidElement, useId, useLayoutEffect, useRef, useState } from 'react'
import InfoHint from './InfoHint'
import { kit } from './strings'
import './FormRow.css'

/* The outlined boxes — kept in step with the notch selector in FormRow.css. */
const OUTLINED =
  '.text-field, .form-textarea, .field-group, .select, .datepicker--field, .datetime-picker, .date-range, .color-field, .timepicker--field'

/**
 * Label + control pair, with what the control has to say for itself underneath.
 * `children` is called with the id so the label stays wired to the control.
 *
 * @param {string} [className]  layout only — `form-narrow` for a control that
 *   should not stretch to the sheet's full width, `form-span` inside FormCols.
 * @param {string} [hint]  guidance ABOUT the field, behind an "i" beside the label.
 *   A form of ten fields carrying ten lines of guidance nobody reads twice stops
 *   looking like a form, so longer guidance stays one tap away.
 * @param {string} [helper]  the one SHORT line a field cannot be filled without —
 *   a format ("مثال: name@org.sa"), a limit, a consequence. Shown under the control
 *   and read with it (`aria-describedby`). Opt-in, and rare by design: use it only
 *   where the answer would otherwise be a failed submit.
 * @param {boolean} [optional]  marks a field the form can be sent WITHOUT, beside the
 *   label — say it on the exception rather than starring the rule.
 *
 * The line under a control is the helper's until there is an error, and then the
 * error's: something red appearing there always means something is wrong.
 */
export default function FormRow({
  label,
  error,
  hint,
  helper,
  required = false,
  optional = false,
  className = '',
  children,
}) {
  const id = useId()
  const errorId = `${id}-error`
  const helperId = `${id}-helper`

  /*
   * IS THE FIELD EMPTY? (2026-10-22, the client's reference.) An outlined field shows its
   * label INSIDE the box, as the placeholder, while it is empty and not being typed in, and
   * lifts it onto the top edge otherwise (FormRow.css). CSS cannot tell on its own — the
   * kit's Select is a button, and most fields carry no placeholder for `:placeholder-shown`
   * to see — so the control is read after every render, which is when a controlled value
   * changes. A field with something at its start (an icon, a fixed prefix, a date
   * picker's calendar) never takes the label inside: the two would sit on each other.
   */
  const controlRef = useRef(null)
  const [empty, setEmpty] = useState(false)
  /* No dependency list on purpose: a controlled value changes by re-rendering this row, so
     every render is when to look. It cannot loop — the state only moves when the answer
     does. */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    const root = controlRef.current
    if (!root) return
    let next = false
    /* Only an outlined box takes its label inside — the same boxes FormRow.css notches. */
    const lead = root.firstElementChild
    const boxed = [lead, lead?.firstElementChild].some((element) => element?.matches?.(OUTLINED))
    if (
      boxed &&
      !root.querySelector('.field-group__icon, .field-group__affix, .datepicker__shell, .datetime-picker, .timepicker--field')
    ) {
      const field = root.querySelector(
        'input:not([type=hidden]):not([type=checkbox]):not([type=radio]):not([type=file]), textarea',
      )
      if (field) next = field.value === ''
      else next = Boolean(root.querySelector('.select__control.is-empty, .date-range__value.is-empty'))
    }
    setEmpty((previous) => (previous === next ? previous : next))
  })
  /* Typing into an uncontrolled field re-renders nothing, so the input event answers too. */
  const onControlInput = (event) => {
    const target = event.target
    if (!target.matches?.('input, textarea')) return
    const lead = controlRef.current?.firstElementChild
    const boxed = [lead, lead?.firstElementChild].some((element) => element?.matches?.(OUTLINED))
    if (boxed) setEmpty(target.value === '')
  }
  /* What the control is described by: the error while there is one, the helper
     otherwise. */
  const describedBy = error ? errorId : helper ? helperId : undefined

  /*
   * A LABEL POINTING AT NOTHING IS NOT A LABEL.
   *
   * The render-prop form wires `htmlFor` to the control because the caller passes the
   * id on. A caller who writes the control as an ORDINARY CHILD — `<FormRow label="…">
   * <TextField … /></FormRow>` — would otherwise get a `<label htmlFor={id}>` whose id no
   * element carried, so the id is bound here instead of being the caller's job. A
   * control that brought its own id keeps it, and the label follows it.
   */
  const single = typeof children !== 'function' && Children.count(children) === 1
    ? Children.only(children)
    : null
  const bindable = isValidElement(single) ? single : null
  const controlId = bindable?.props?.id ?? id

  let control
  if (typeof children === 'function') {
    control = children(id, describedBy)
  } else if (bindable && !bindable.props.id) {
    control = cloneElement(bindable, {
      id,
      ...(describedBy ? { 'aria-describedby': describedBy } : null),
    })
  } else {
    control = children
  }

  return (
    <div
      className={`form-row${error ? ' form-row--invalid' : ''}${empty ? ' form-row--inside' : ''} ${className}`.trim()}
    >
      <label className="form-row__label" htmlFor={controlId}>
        {/* `required` marks a field the form cannot be sent without, BEFORE it is sent.
            The star is for the eye; the label itself carries the word for a reader. */}
        <bdi>
          {label}
          {required && (
            <span className="form-row__required">
              <span aria-hidden="true">*</span>
              <span className="sr-only">{kit.fieldRequired}</span>
            </span>
          )}
        </bdi>
        {optional && !required && <span className="form-row__optional">({kit.fieldOptional})</span>}
        {hint && <InfoHint content={<bdi>{hint}</bdi>} />}
      </label>
      <div className="form-row__control" ref={controlRef} onInput={onControlInput}>
        {control}
      </div>
      {error ? (
        <p className="form-row__error" id={errorId} role="alert">
          <bdi>{error}</bdi>
        </p>
      ) : (
        helper && (
          <p className="form-row__helper" id={helperId}>
            <bdi>{helper}</bdi>
          </p>
        )
      )}
    </div>
  )
}
